import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';

// ─── Icons ───────────────────────────────────────────────────────
const BackIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </svg>
);
const ExternalIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);

const SUB_STATE = {
  NEW: { label: 'Not started', color: 'text-gray-400' },
  CREATED: { label: 'Assigned', color: 'text-gray-500' },
  TURNED_IN: { label: 'Turned in', color: 'text-brand-600' },
  RETURNED: { label: 'Returned', color: 'text-green-600' },
  RECLAIMED_BY_STUDENT: { label: 'Reclaimed', color: 'text-yellow-600' },
};

// ═══════════════════════════════════════════════════════════════════
// Main ClassroomPage — pulls directly from Google Classroom
// ═══════════════════════════════════════════════════════════════════

const ClassroomPage = () => {
  const [gcCourses, setGcCourses] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setMessage(''); }
    else { setMessage(msg); setError(''); }
    setTimeout(() => { setMessage(''); setError(''); }, 4000);
  };

  const fetchData = async () => {
    try {
      const statusRes = await api.get('/google/classroom/status');
      setConnected(statusRes.data.connected);

      if (statusRes.data.connected) {
        const coursesRes = await api.get('/google/classroom/courses');
        setGcCourses(coursesRes.data.courses || []);
      }
    } catch (err) {
      console.error('Classroom fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load Classroom data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="text-gray-500">Loading...</div>;

  if (!connected) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Google Classroom</h1>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Classroom Integration</h3>
          <p className="text-sm text-gray-500 mb-1">Coming soon! Sync courses, manage assignments, and track grades — all from DanceHall.</p>
          <p className="text-sm text-gray-400 mb-4">In the meantime, manage your classroom directly.</p>
          <a
            href="https://classroom.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            Open Google Classroom
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
          </a>
        </div>
      </div>
    );
  }

  // ─── Course Detail View ─────────────────────────────────────────
  if (selectedCourse) {
    return (
      <CourseDetailView
        course={selectedCourse}
        onBack={() => setSelectedCourse(null)}
        flash={flash}
        message={message}
        error={error}
      />
    );
  }

  // ─── Course List (from Google Classroom) ────────────────────────
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Google Classroom</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
            </svg>
            Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-brand-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            + Create Class
          </button>
          <div className="flex items-center gap-1.5 text-sm text-green-600">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            Connected
          </div>
        </div>
      </div>

      {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {/* Create class form */}
      {showCreate && (
        <CreateClassForm
          onCreated={() => { setShowCreate(false); fetchData(); }}
          onCancel={() => setShowCreate(false)}
          flash={flash}
        />
      )}

      {gcCourses.length === 0 && !showCreate ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
          </svg>
          <p className="text-gray-500 mb-4">No classes in Google Classroom yet.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            Create Your First Class
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gcCourses.map(course => (
            <div
              key={course.id}
              onClick={() => setSelectedCourse(course)}
              className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden cursor-pointer hover:shadow-md hover:border-brand-200 transition-all group"
            >
              <div className="h-20 bg-gradient-to-r from-brand-500 to-brand-400 p-4 flex flex-col justify-end">
                <h3 className="font-bold text-white text-lg truncate">{course.name}</h3>
                {course.section && <p className="text-brand-100 text-sm truncate">{course.section}</p>}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  {course.descriptionHeading && (
                    <p className="text-sm text-gray-500 truncate">{course.descriptionHeading}</p>
                  )}
                  {course.courseState === 'PROVISIONED' && (
                    <span className="text-[10px] font-medium bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">Draft</span>
                  )}
                </div>
                {course.enrollmentCode && (
                  <p className="text-xs text-gray-400 mt-1">Code: {course.enrollmentCode}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-brand-500 font-medium group-hover:text-brand-600">Open class &rarr;</span>
                  <a
                    href={course.alternateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-xs text-gray-400 hover:text-brand-500 flex items-center gap-1"
                  >
                    <ExternalIcon />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Create Class Form ───────────────────────────────────────────

const CreateClassForm = ({ onCreated, onCancel, flash }) => {
  const [form, setForm] = useState({ name: '', section: '', description: '', room: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post('/google/classroom/courses', {
        name: form.name.trim(),
        section: form.section.trim() || undefined,
        description: form.description.trim() || undefined,
        room: form.room.trim() || undefined
      });
      flash('Class created in Google Classroom');
      onCreated();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to create class', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-brand-200 shadow-sm p-5 mb-6">
      <h3 className="font-semibold text-gray-900 mb-4">Create a Class</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Class Name</label>
          <input
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            placeholder="Enter class name"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
          <input
            value={form.section}
            onChange={e => setForm(p => ({ ...p, section: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            placeholder="Enter section (optional)"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
          <input
            value={form.room}
            onChange={e => setForm(p => ({ ...p, room: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            placeholder="Enter room (optional)"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none"
            placeholder="Enter description (optional)"
          />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
        <button
          type="submit"
          disabled={saving || !form.name.trim()}
          className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
        >
          {saving ? 'Creating...' : 'Create'}
        </button>
      </div>
    </form>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Course Detail — tabs: Classwork, People, Grades
// gcCourseId = course.id (Google Classroom ID)
// ═══════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'classwork', label: 'Classwork' },
  { key: 'people', label: 'People' },
  { key: 'grades', label: 'Grades' },
];

const CourseDetailView = ({ course, onBack, flash, message, error }) => {
  const [activeTab, setActiveTab] = useState('classwork');
  const gcId = course.id; // Google Classroom course ID

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1">
        <BackIcon /> All Classes
      </button>

      {/* Banner */}
      <div className="rounded-xl bg-gradient-to-r from-brand-500 to-brand-400 p-6 mb-6">
        <h1 className="text-2xl font-bold text-white">{course.name}</h1>
        <p className="text-brand-100 mt-1">
          {[course.section, course.descriptionHeading, course.room].filter(Boolean).join(' \u00b7 ')}
        </p>
        <div className="flex items-center gap-3 mt-4">
          {course.enrollmentCode && (
            <span className="px-3 py-1.5 text-sm font-medium rounded-lg bg-white/20 text-white">
              Code: {course.enrollmentCode}
            </span>
          )}
          <a
            href={course.alternateLink}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-sm text-white/80 hover:text-white font-medium flex items-center gap-1"
          >
            Open in Classroom <ExternalIcon />
          </a>
        </div>
      </div>

      {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'classwork' && <ClassworkTab gcCourseId={gcId} flash={flash} />}
      {activeTab === 'people' && <PeopleTab gcCourseId={gcId} />}
      {activeTab === 'grades' && <GradesTab gcCourseId={gcId} flash={flash} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Classwork Tab
// ═══════════════════════════════════════════════════════════════════

const ClassworkTab = ({ gcCourseId, flash }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const fetchAssignments = useCallback(async () => {
    try {
      const { data } = await api.get(`/google/classroom/gc/${gcCourseId}/coursework`);
      setAssignments(data.courseWork || []);
    } catch (err) {
      flash('Failed to load classwork', true);
    } finally {
      setLoading(false);
    }
  }, [gcCourseId, flash]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  if (selectedAssignment) {
    return (
      <SubmissionsView
        gcCourseId={gcCourseId}
        assignment={selectedAssignment}
        onBack={() => setSelectedAssignment(null)}
        flash={flash}
      />
    );
  }

  if (loading) return <div className="text-gray-500 text-sm">Loading classwork...</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => { setCreateType('assignment'); setShowCreate(true); }}
          className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          + Assignment
        </button>
        <button
          onClick={() => { setCreateType('announcement'); setShowCreate(true); }}
          className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          + Announcement
        </button>
      </div>

      {showCreate && createType === 'assignment' && (
        <CreateAssignmentForm
          gcCourseId={gcCourseId}
          onCreated={() => { setShowCreate(false); setCreateType(null); fetchAssignments(); }}
          onCancel={() => { setShowCreate(false); setCreateType(null); }}
          flash={flash}
        />
      )}
      {showCreate && createType === 'announcement' && (
        <CreateAnnouncementForm
          gcCourseId={gcCourseId}
          onCreated={() => { setShowCreate(false); setCreateType(null); }}
          onCancel={() => { setShowCreate(false); setCreateType(null); }}
          flash={flash}
        />
      )}

      {assignments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <p className="text-gray-500">No classwork yet. Create an assignment to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {assignments.map(a => {
            const due = a.dueDate ? new Date(a.dueDate.year, a.dueDate.month - 1, a.dueDate.day) : null;
            const isPast = due && due < new Date();
            return (
              <div
                key={a.id}
                onClick={() => setSelectedAssignment(a)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:border-brand-200 hover:shadow-md transition-all flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{a.title}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span>Posted {new Date(a.creationTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    {due && (
                      <span className={isPast ? 'text-red-500' : ''}>
                        Due {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {isPast && ' (past)'}
                      </span>
                    )}
                    {a.maxPoints && <span>{a.maxPoints} pts</span>}
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Create Assignment Form ──────────────────────────────────────

const CreateAssignmentForm = ({ gcCourseId, onCreated, onCancel, flash }) => {
  const [form, setForm] = useState({ title: '', description: '', dueDate: '', maxPoints: 100 });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/google/classroom/gc/${gcCourseId}/coursework`, {
        title: form.title.trim(),
        description: form.description.trim(),
        dueDate: form.dueDate || undefined,
        maxPoints: Number(form.maxPoints) || 100
      });
      flash('Assignment created');
      onCreated();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to create assignment', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-brand-200 shadow-sm p-5 mb-6">
      <h3 className="font-semibold text-gray-900 mb-4">New Assignment</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            placeholder="Enter assignment title" required />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none"
            placeholder="Enter instructions for students" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
          <input type="number" min="0" max="1000" value={form.maxPoints} onChange={e => setForm(p => ({ ...p, maxPoints: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent" />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
        <button type="submit" disabled={saving || !form.title.trim()}
          className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
          {saving ? 'Creating...' : 'Create'}
        </button>
      </div>
    </form>
  );
};

// ─── Create Announcement Form ────────────────────────────────────

const CreateAnnouncementForm = ({ gcCourseId, onCreated, onCancel, flash }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/google/classroom/gc/${gcCourseId}/announcements`, { text: text.trim() });
      flash('Announcement posted');
      onCreated();
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to post announcement', true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handlePost} className="bg-white rounded-xl border border-brand-200 shadow-sm p-5 mb-6">
      <h3 className="font-semibold text-gray-900 mb-4">New Announcement</h3>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none mb-4"
        placeholder="Share something with your class..." required />
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
        <button type="submit" disabled={saving || !text.trim()}
          className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
          {saving ? 'Posting...' : 'Post'}
        </button>
      </div>
    </form>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Submissions View
// ═══════════════════════════════════════════════════════════════════

const SubmissionsView = ({ gcCourseId, assignment, onBack, flash }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState({});
  const [gradingLoading, setGradingLoading] = useState({});

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data } = await api.get(`/google/classroom/gc/${gcCourseId}/coursework/${assignment.id}/submissions`);
      setSubmissions(data.submissions || []);
    } catch (err) {
      flash('Failed to load submissions', true);
    } finally {
      setLoading(false);
    }
  }, [gcCourseId, assignment.id, flash]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleGrade = async (subId) => {
    const grade = Number(grading[subId]);
    if (isNaN(grade) || grade < 0) return;
    setGradingLoading(prev => ({ ...prev, [subId]: true }));
    try {
      await api.patch(`/google/classroom/gc/${gcCourseId}/coursework/${assignment.id}/submissions/${subId}/grade`, { grade });
      flash(`Graded ${grade}/${assignment.maxPoints || 100}`);
      fetchSubmissions();
      setGrading(prev => { const n = { ...prev }; delete n[subId]; return n; });
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to grade', true);
    } finally {
      setGradingLoading(prev => ({ ...prev, [subId]: false }));
    }
  };

  const due = assignment.dueDate ? new Date(assignment.dueDate.year, assignment.dueDate.month - 1, assignment.dueDate.day) : null;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1">
        <BackIcon /> Back to Classwork
      </button>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{assignment.title}</h2>
        {assignment.description && <p className="text-sm text-gray-500 mt-1">{assignment.description}</p>}
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
          {due && <span>Due: {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
          <span>{assignment.maxPoints || 100} points</span>
          {assignment.alternateLink && (
            <a href={assignment.alternateLink} target="_blank" rel="noopener noreferrer"
              className="text-brand-500 hover:text-brand-600 font-medium flex items-center gap-1 ml-auto">
              Open in Classroom <ExternalIcon />
            </a>
          )}
        </div>
      </div>

      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Submissions ({submissions.length})
      </h3>

      {loading ? (
        <div className="text-gray-500 text-sm">Loading submissions...</div>
      ) : submissions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <p className="text-gray-500">No submissions yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-5 py-3">Student</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Grade</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(sub => {
                const stateInfo = SUB_STATE[sub.state] || { label: sub.state, color: 'text-gray-500' };
                return (
                  <tr key={sub.id} className="border-b border-gray-50 last:border-b-0">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-gray-900 text-sm">{sub.studentName}</div>
                      <div className="text-xs text-gray-400">{sub.studentEmail}</div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-sm font-medium ${stateInfo.color}`}>{stateInfo.label}</span>
                      {sub.late && <span className="text-xs text-red-500 font-medium ml-2">Late</span>}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {sub.assignedGrade !== undefined && sub.assignedGrade !== null ? (
                        <span className="text-sm font-bold text-gray-900">{sub.assignedGrade}/{assignment.maxPoints || 100}</span>
                      ) : (
                        <div className="flex items-center gap-2 justify-end">
                          <input type="number" min="0" max={assignment.maxPoints || 100}
                            value={grading[sub.id] || ''}
                            onChange={e => setGrading(prev => ({ ...prev, [sub.id]: e.target.value }))}
                            placeholder="--"
                            className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-400" />
                          <span className="text-xs text-gray-400">/{assignment.maxPoints || 100}</span>
                          <button onClick={() => handleGrade(sub.id)}
                            disabled={gradingLoading[sub.id] || !grading[sub.id]}
                            className="bg-brand-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
                            {gradingLoading[sub.id] ? '...' : 'Return'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// People Tab
// ═══════════════════════════════════════════════════════════════════

const PeopleTab = ({ gcCourseId }) => {
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/google/classroom/gc/${gcCourseId}/students`);
        setTeachers(data.teachers || []);
        setStudents(data.students || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load people');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [gcCourseId]);

  if (loading) return <div className="text-gray-500 text-sm">Loading people...</div>;
  if (error) return <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>;

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Teachers <span className="text-xs text-gray-400 normal-case font-normal">({teachers.length})</span>
        </h3>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
          {teachers.map(t => (
            <div key={t.userId} className="px-5 py-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-sm flex-shrink-0">
                {t.name?.charAt(0) || '?'}
              </div>
              <div>
                <div className="font-medium text-gray-900 text-sm">{t.name}</div>
                <div className="text-xs text-gray-400">{t.email}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Students <span className="text-xs text-gray-400 normal-case font-normal">({students.length})</span>
        </h3>
        {students.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <p className="text-gray-500 text-sm">No students have joined this class yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
            {students.map(s => (
              <div key={s.userId} className="px-5 py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-sm flex-shrink-0">
                  {s.name?.charAt(0) || '?'}
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-sm">{s.name}</div>
                  <div className="text-xs text-gray-400">{s.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Grades Tab (grade book matrix)
// ═══════════════════════════════════════════════════════════════════

const GradesTab = ({ gcCourseId, flash }) => {
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/google/classroom/gc/${gcCourseId}/grades`);
        setStudents(data.students || []);
        setAssignments(data.assignments || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load grades');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [gcCourseId]);

  if (loading) return <div className="text-gray-500 text-sm">Loading grades...</div>;
  if (error) return <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>;

  if (students.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <p className="text-gray-500">No students in this class yet.</p>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <p className="text-gray-500">No assignments yet. Create one in the Classwork tab.</p>
      </div>
    );
  }

  const studentOverall = students.map(s => {
    let earned = 0, total = 0;
    for (const a of assignments) {
      const sub = a.submissions[s.userId];
      if (sub?.assignedGrade !== null && sub?.assignedGrade !== undefined) {
        earned += sub.assignedGrade;
        total += a.maxPoints;
      }
    }
    return { ...s, earned, total, pct: total > 0 ? Math.round((earned / total) * 100) : null };
  });

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            <th className="px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-white min-w-[180px]">Student</th>
            {assignments.map(a => (
              <th key={a.id} className="px-3 py-3 font-medium text-gray-600 text-center min-w-[100px]">
                <div className="truncate max-w-[100px]" title={a.title}>{a.title}</div>
                <div className="text-xs text-gray-400 font-normal">{a.maxPoints} pts</div>
              </th>
            ))}
            <th className="px-4 py-3 font-semibold text-gray-700 text-center min-w-[80px]">Overall</th>
          </tr>
        </thead>
        <tbody>
          {studentOverall.map(s => (
            <tr key={s.userId} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50">
              <td className="px-4 py-3 sticky left-0 bg-white">
                <div className="font-medium text-gray-900">{s.name}</div>
              </td>
              {assignments.map(a => {
                const sub = a.submissions[s.userId];
                const grade = sub?.assignedGrade;
                const state = sub?.state;
                let cellContent;
                if (grade !== null && grade !== undefined) {
                  const pct = Math.round((grade / a.maxPoints) * 100);
                  cellContent = (
                    <span className={`font-medium ${pct >= 70 ? 'text-green-700' : pct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {grade}
                    </span>
                  );
                } else if (state === 'TURNED_IN') {
                  cellContent = <span className="text-brand-500 text-xs font-medium">Turned in</span>;
                } else {
                  cellContent = <span className="text-gray-300">-</span>;
                }
                return (
                  <td key={a.id} className="px-3 py-3 text-center">
                    {cellContent}
                    {sub?.late && <div className="text-[10px] text-red-400">late</div>}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-center">
                {s.pct !== null ? (
                  <span className={`font-bold ${s.pct >= 70 ? 'text-green-700' : s.pct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {s.pct}%
                  </span>
                ) : (
                  <span className="text-gray-300">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClassroomPage;
